<template>
	<div class="order-summary-item"
		 ref="order-summary-item"
		 :class="{'swipe-delete': translateX < 0, 'static': translateX === 0, counter: isInCounter}"
		 :style="'--translate-x: ' + translateX + 'px;height: ' + heightProp + ';'"
		 v-swipe="swipeHandler" >
		<div class="item-container" :class="{pointer: !disableClick && !!item && isInCounter}" @click="clickItem" v-longpress="{handler: longClickItem}">
			<div class="image-container">
				<img v-if="!!item" :src="itemImage">
				<div v-if="!item" class="ghost-image-placeholder"></div>
			</div>
			<div class="item-info" :class="{deletable}" v-if="!!item">
				<div class="item-name-container">
					<div class="item-name" :class="{done: showAsPrepared}" :title="showAsPrepared ? getString('prepared') : ''">{{ item.menu_item ? item.menu_item.name : '-' }}</div>
					<div v-if="showPreparedIcon && !showAsPrepared" class="prepare-icon" :class="{disabled: loadingPrepared, dark: darkBackground && userStore.darkMode}" v-html="svgStore.get('iconPaperPlane')" :title="getString('prepared')" @click.stop="markAsPrepared"></div>
				</div>
				<div class="item-info-data">
					<div class="item-quantity-container">
						<div class="item-quantity">{{ itemQuantity }}</div>
						<div class="paid-indicator-container" v-if="withSymbol && item.redeemed > 0">
							<img class="paid-symbol" :title="getString('item_redeemed')" :class="{paid: item.redeemed === item.quantity}" :src="tickIconSrc" alt="check">
							<div class="paid-indicator">x{{ item.redeemed }}</div>
						</div>
					</div>
					<div class="item-price"><span v-if="itemDiscountedTotal" class="item-price-discount">{{itemDiscountedTotal}}</span>{{ itemTotal }}</div>
				</div>
			</div>
			<div v-if="!!item && deletable" class="item-delete" @click.stop="$emit('delete', 1)">
				<img :src="'https://img.qromo.io/img/icons/icon-bin-counter-blue'+(userStore.darkMode ? '-dark' : '')+'.svg'">
			</div>
			<div class="item-info" v-if="!item">
				<div class="item-name ghost-placeholder long"></div>
				<div class="item-info-data">
					<div class="item-quantity ghost-placeholder short"></div>
					<div class="item-price ghost-placeholder short"></div>
				</div>
			</div>
		</div>
		<div class="item-products-container" :class="{done: showAsPrepared}" v-if="!!item && !!item.products && item.products.length > 0">
			<div class="item-product" v-for="product in item.products">
				<div class="item-product-name">{{ "+ "+product.product_name }}</div>
				<div class="item-product-info-data">
					<div class="item-product-quantity">{{ "x"+product.quantity }}</div>
					<div class="item-product-price">{{ currencyStore.readableCurrencyValue(product.price*product.quantity) }}</div>
				</div>
			</div>
		</div>
		<div class="notes-container" v-if="!!item && !!item.note">
			<div class="notes-title">{{ getString('item_notes') }}</div>
			<div class="notes-body">{{ item.note }}</div>
		</div>
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useCounterStore} from "@/console-new/stores/counter.store";
import {useMenuStore, useOrdersStore} from "@/console-new/stores";
import {useUserStore} from "@/console-new/stores/user.store";
import API from "@/shared/helpers/API";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		const ordersStore = useOrdersStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			languageStore,
			counterStore,
			menuStore,
			currencyStore,
			userStore,
			svgStore,
			ordersStore
		}
	},
	name: "OrderSummaryItem",
	emits: ["delete"],
	props: {
		item: {
			type: Object,
			default: null,
			validator: value => !value || (value.hasOwnProperty("menu_item") && value.hasOwnProperty("price") && value.hasOwnProperty("quantity"))
		},
		deletable: {
			type: Boolean,
			default: false
		},
		isInCounter: {
			type: Boolean,
			default: false
		},
		done: {
			type: Boolean,
			default: false
		},
		withSymbol: {
			type: Boolean,
			default: false
		},
		disableClick: {
			type: Boolean,
			default: false
		},
		showPreparedIcon: {
			type: Boolean,
			default: false
		},
		darkBackground: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			translateX: 0,
			endingAnimation: false,
			loadingPrepared: false,
			heightProp: 'auto',
			scrollElements: [],
			scrollElementsMap: {},
		}
	},
	computed: {
		showAsPrepared() {
			return this.done || (this.showPreparedIcon && this.item.prepared)
		},
		tickIconSrc() {
			return 'https://img.qromo.io/img/icons/icon-rounded-ok-blu'+(this.userStore.darkMode ? '-dark' : '')+'.svg';
		},
		itemTotal() {
			return this.currencyStore.readableCurrencyValue(this.item.price*this.item.quantity);
		},
		itemDiscountedTotal() {
			return this.item.fullPrice ? this.currencyStore.readableCurrencyValue(this.item.fullPrice*this.item.quantity) : null;
		},
		itemQuantity() {
			return "x"+this.item.quantity;
		},
		itemImage() {
			if(this.item?.menu_item?.picture) {
				return this.item.menu_item.picture;
			} else {
				let category = this.menuStore.categoryFromItemId(this.item.menu_item_id);
				if(category && category.taxonomy) {
					return URL_HTML+'/img/categories/'+category.taxonomy+'.php?c=0752a3&b=bfd0e4';
				} else {
					return URL_HTML+'/img/categories/icon-generic-food-drink-1.php?c=0752a3&b=bfd0e4';
				}
			}
		},
		allowSwipe() {
			return this.isInCounter && this.deletable && !this.endingAnimation && this.item !== null;
		},
		isDeviceTouch() {
			return (('ontouchstart' in window) ||
				(navigator.maxTouchPoints > 0) ||
				(navigator.msMaxTouchPoints > 0));
		}
	},
	methods: {
		swipeHandler(trigger, isRight, translationX) {
			if(this.allowSwipe) {
				this.translateX = translationX > 0 ? 0 : translationX;
				if(trigger && isRight) {
					this.startEndingAnimation();
				}
			}
		},
		clickItem() {
			if(this.disableClick){
				return;
			}

			if(this.item && this.isInCounter) {
				if(this.isDeviceTouch) {
					this.item.quantity++;
				} else {
					this.counterStore.editingItem = this.item;
				}
			}
		},
		longClickItem() {
			if(this.disableClick){
				return;
			}

			if(this.item && this.isInCounter) {
				if(this.isDeviceTouch) {
					this.counterStore.editingItem = this.item;
				}
			}
		},
		startEndingAnimation() {
			this.endingAnimation = true;

			if(this.$refs["order-summary-item"]) {
				let clientRect = this.$refs["order-summary-item"].getBoundingClientRect();
				this.translateX = -clientRect.width;
				this.heightProp = clientRect.height + "px";
				setTimeout(() => {
					this.$refs["order-summary-item"].classList.add("disappearing");
					setTimeout(() => {
						this.$emit("delete", -1);
					}, 200);
				}, 20);
			}
		},
		markAsPrepared() {
			const orderItemId = this.item?.order_item_id || this.item?.identifier;
			if(!this.item || this.loadingPrepared || !orderItemId) return;
			this.loadingPrepared = true;
			API.init().setOrderItemPrepared(this.userStore.currBusinessId, orderItemId, true, (r) => {
				if(r.result === "OK") {
					const orderGroup = this.ordersStore.selectedOrderGroup;
					if(orderGroup) {
						for(const order of orderGroup.orders) {
							const storeItem = order.items.find(i => i.order_item_id === orderItemId);
							if(storeItem) {
								storeItem.prepared = true;
								break;
							}
						}
					}
				} else {
					this.showNotification('generic-fail')
				}
				this.loadingPrepared = false;
			});
		}
	},
}
</script>

<style lang="scss" scoped>
.order-summary-item {
	display: flex;
	flex-direction: column;
	width: 100%;
	position: relative;
	z-index: 2;
	--translate-x: 0;
	transform: translateX(var(--translate-x));
	transition: transform 0.15s ease, height 0.2s linear, padding 0.2s linear, background-color 0.2s linear;

	&.static {
		transform: none;
	}

	&.swipe-delete {
		transition: transform 0.01s ease, height 0.2s linear, padding 0.2s linear, background-color 0.2s linear;
		background-color: var(--color-bg-primary-2);
	}

	&.disappearing {
		height: 0 !important;
		padding: 0 !important;
	}

	&.counter {
		box-shadow: 2px 0 2px -2px rgba(0, 0, 0, 0.8);
		background-color: var(--color-bg);
	}

	.item-container {
		display: flex;
		align-items: stretch;
		justify-content: space-between;
		position: relative;
		z-index: 3;

		* {
			user-select: none;
		}

		.image-container {
			width: 45px;
			height: 45px;
			border-radius: 13px;
			margin-right: 10px;
			background-color: var(--color-bg-item);

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
				border-radius: 13px;
			}
		}

		.item-info {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			width: calc(100% - 55px);

			.item-name-container {
				display: flex;
				align-items: center;
				gap: 3px;
				margin-bottom: 4px;
			}

			.item-name {
				font-size: 16px;
				font-weight: 700;
				min-height: 22px;
				color: var(--color-txt);
				@include truncateString();

				&.done {
					text-decoration: line-through;
				}
			}

			.prepare-icon {
				cursor: pointer;
				height: 22px;
				width: 22px;
				border-radius: 7px;
				display: flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				margin-left: auto;
				background-color: var(--color-alpha-1);

				&.dark {
					background-color: var(--color-bg);
				}

				&:deep(svg) {
					width: 21px;
					height: 21px;
					.fill {
						fill: var(--color-primary);
					}
				}
			}

			.item-info-data {
				display: flex;
				justify-content: space-between;
				align-items: flex-end;
				width: 100%;

				.item-quantity-container {
					display: flex;
					align-items: center;
					justify-content: flex-start;
					gap: 4px;

					.item-quantity {
						font-size: 16px;
						color: var(--color-primary);
					}

					.paid-indicator-container {
						display: flex;
						align-items: center;
						justify-content: center;
						height: 20px;
						background-color: var(--color-alpha-1);
						border-radius: 7px;
						font-size: 12px;
						gap: 2px;
						padding: 0 4px;

						.paid-symbol {
							height: 14px;
							width: 14px;
							border-radius: 7px;
							display: flex;
							align-items: center;
							justify-content: center;

							p {
								font-size: 11px;
								color: var(--color-primary);
								margin-left: -1px;
							}

							&.paid {
								background-color: var(--color-secondary);
							}

						}
					}

				}

				.item-price {
					font-size: 14px;
					color: var(--color-txt);

					.item-price-discount {
						font-size: 12px;
						color:var(--color-txt-1);
						text-decoration: line-through;
						margin-right: 4px;
					}
				}
			}

			&.deletable {
				width: calc(100% - 83px);
			}
		}

		.item-delete {
			width: 20px;
			overflow: hidden;
			display: flex;
			justify-content: center;
			align-items: center;
			margin-left: 8px;
			cursor: pointer;

			img {
				width: 100%;
				padding: 3px;
			}
		}
	}

	.item-products-container {
		display: flex;
		flex-direction: column;
		width: calc(100% - 45px);
		margin-top: 8px;
		margin-left: 45px;
		padding: 8px 10px;
		background-color: var(--color-alpha-1);
		box-shadow: inset 0 2px 3px var(--color-box-shadow-2);
		border-radius: 13px;

		.item-product {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			width: 100%;

			&::after {
				content: "";
				display: block;
				width: 100%;
				height: 1px;
				background-color: var(--color-box-shadow-1);
				margin-top: 6px;
				margin-bottom: 6px;
			}

			.item-product-name {
				font-size: 14px;
				color: var(--color-txt);
				@include truncateString();
			}
		}

		&.done .item-product-name {
			text-decoration: line-through;
		}

		.item-product {
			.item-product-info-data {
				display: flex;
				justify-content: space-between;
				align-items: flex-end;
				width: 100%;
				margin-top: 2px;

				.item-product-quantity {
					font-size: 14px;
					color: $primary;
				}

				.item-product-price {
					font-size: 14px;
					color: var(--color-txt);
				}
			}
		}

		.item-product:last-child {
			&::after {
				content: none;
			}
		}
	}

	.notes-container {
		margin-top: 8px;

		.notes-title {
			font-size: 14px;
			color: var(--color-txt);
			font-weight: 700;
		}

		.notes-body {
			font-size: 14px;
			color: var(--color-txt);
			white-space: pre-wrap;
			word-wrap: break-word;
		}
	}

	.ghost-image-placeholder {
		width: 45px;
		height: 45px;
		background-color: var(--color-bg-primary-1);
		border-radius: 13px;
	}

	.ghost-placeholder {
		width: 60px;
		height: 18px;
		background-color: $alpha_1;
		border-radius: 4px;

		&.long {
			width: 110px;
		}

		&.short {
			width: 40px;
		}
	}

}
</style>